import { apiUrl } from './api';
import type { UserRole } from '../types';

/**
 * The authenticated half of the API.
 *
 * Every call to the economy has to say who is asking and which guild it is
 * asking about. Rather than thread both through a dozen components, the session
 * registers them here once at boot and this module attaches them.
 *
 * The access token is held in a module variable and, as everywhere else in this
 * app, is **never persisted** — it lives for the page's lifetime and no longer.
 */

interface Credentials {
  guildId: string;
  /** Present inside Discord; null in the browser preview and on the studio. */
  accessToken: string | null;
  /** Preview only: the identity the dev backend should act as. */
  devUser: { id: string; username: string; displayName: string } | null;
  devRole: UserRole;
  /**
   * Studio only: the session cookie authenticates instead of a header.
   *
   * Which is the whole reason no token appears in this module on the web — the
   * credential stays sealed on the server and the browser only carries a cookie
   * it cannot read.
   */
  cookie?: boolean;
}

/**
 * The guild the browser preview plays in. It is a valid snowflake shape but not
 * a real server, so preview spins land in their own save file and can't touch a
 * live economy — and a production server, whose `SPOYL_GUILD_IDS` allow-list
 * never names it, refuses it outright.
 */
export const PREVIEW_GUILD_ID = '0';

let credentials: Credentials | null = null;

/**
 * The model profile every request is scoped to. Set by the session when a
 * profile is picked (players) or resolved (owner, managers), for the same
 * reason the guild id lives here: every call to the economy has to say which
 * profile it is about, and threading it through every component would be noise.
 */
let activeProfileId: string | null = null;

export function configureApi(next: Credentials): void {
  credentials = next;
}

export function setActiveProfile(profileId: string | null): void {
  activeProfileId = profileId;
}

/** The guild id the API is currently talking about, for callers that need it. */
export function currentGuildId(): string | null {
  return credentials?.guildId ?? null;
}

function authHeaders(): Record<string, string> {
  if (!credentials) return {};
  if (credentials.accessToken) {
    return { Authorization: `Bearer ${credentials.accessToken}` };
  }
  if (credentials.devUser) {
    return {
      'x-spoyl-dev-user-id': credentials.devUser.id,
      'x-spoyl-dev-username': credentials.devUser.username,
      'x-spoyl-dev-name': credentials.devUser.displayName,
      'x-spoyl-dev-role': credentials.devRole,
    };
  }
  return {};
}

function withScope(path: string): string {
  const guildId = credentials?.guildId;
  if (!guildId) return path;
  let scoped = `${path}${path.includes('?') ? '&' : '?'}guildId=${encodeURIComponent(guildId)}`;
  // The profile travels the same way as the guild: routes that aren't about
  // one profile's economy (identity, the registry) simply ignore it.
  if (activeProfileId) scoped += `&profileId=${encodeURIComponent(activeProfileId)}`;
  return scoped;
}

/** The message the server sent, when it sent one — otherwise the status. */
async function describe(response: Response, path: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string') return body.error;
  } catch {
    // Not JSON; fall through to the generic message.
  }
  return `Request to ${path} failed with status ${response.status}`;
}

/**
 * Sends a file as the raw request body — what the mascot uploader needs, since
 * `request` speaks JSON. Same credentials and same guild/profile scoping as every
 * other call; the content type is the file's own, and the server decides what it
 * really is from its first bytes rather than believing this header.
 */
export async function uploadFile<T>(path: string, file: Blob): Promise<T> {
  const response = await fetch(apiUrl(withScope(path)), {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
    ...(credentials?.cookie ? { credentials: 'include' as const } : {}),
  });

  if (!response.ok) {
    throw new Error(await describe(response, path));
  }
  return (await response.json()) as T;
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  // The guild travels in the query string for every verb: it identifies the
  // economy being addressed, which is as true of a POST as it is of a read.
  const response = await fetch(apiUrl(withScope(path)), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...init?.headers,
    },
    // Stated rather than left to the default, which drops cookies the moment a
    // deployment puts the studio and this API on different hosts.
    ...(credentials?.cookie ? { credentials: 'include' as const } : {}),
    method,
  });

  if (!response.ok) {
    throw new Error(await describe(response, path));
  }

  return (await response.json()) as T;
}
